import { Fragment, useState } from 'react';

import CountUp from 'react-countup';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import ProgressLine from 'Components/ProgressLine';
import { breakpoints } from 'Components/ThemeProvider';
import { Typography } from 'Components/Typography';

import useMedia from 'utils/useMedia';

import ScoringListContentDesktop from './ScoringListContentDesktop';
import ScoringListContentMobile from './ScoringListContentMobile';

const ScoringWrapper = styled('div')`
  position: relative;
  display: flex;
  width: 327px;
  border: 1px solid ${theme.colors.tertiary['200']};
  border-bottom: none;
  flex-wrap: wrap;
  background: ${theme.colors.neutral[100]};
  font-family: ${legacyTheme.typography.simplonNorm};
  font-size: 0.9rem; /* @juliana: 14px */
  letter-spacing: 0.5px;
  text-align: left;
  color: ${theme.colors.primary[400]};

  ${legacyTheme.breakpoints.up('sm')} {
    width: 630px;
  }
  ${legacyTheme.breakpoints.up('md')} {
    width: 100%;
    border: 1px solid ${theme.colors.tertiary['200']};
  }
  ${legacyTheme.breakpoints.up('lg')} {
    width: 550px;
  }
`;

const ScoringLine = styled('button')<{ isLast: boolean }>`
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  height: 59px;
  display: flex;
  align-items: center;
  border-bottom: solid 1px ${theme.colors.tertiary['200']};
  width: 87%;
  padding-left: 14px;

  &:last-of-type {
    ${({ isLast }) => (isLast ? 'border: none;' : '')}
  }

  p {
    width: 94px;
    min-width: 94px;
    text-align: left;
  }

  ${legacyTheme.breakpoints.up('sm')} {
    width: 94%;
    padding-left: 24px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    justify-content: center;
    width: 100%;

    &:hover {
      background: ${theme.colors.tertiary[100]};
    }
  }
`;

const ProgressLineWrapper = styled('div')`
  position: relative;
  width: 83px;
  margin: 7px 17px 7px 0;

  ${legacyTheme.breakpoints.up('sm')} {
    width: 378px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    width: 300px;
  }
`;

const CountContent = styled('div')<{ count: number }>`
  color: ${({ count }) => (count > 0 ? theme.colors.primary[400] : theme.colors.neutral[800])};
  width: 48px;
  text-align: right;
`;

const ScoringChevronMobile = styled('button')<{ isDesktop: boolean }>`
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 59px;
  width: 13%;
  border-left: ${({ isDesktop }) =>
    isDesktop ? 'none' : `solid 1px ${theme.colors.tertiary['200']}`};
  border-bottom: solid 1px ${theme.colors.tertiary['200']};

  ${legacyTheme.breakpoints.up('sm')} {
    width: 6%;
  }
`;
const ScoringChevronDesktop = styled('div')`
  margin: 0 20px;
`;

const ChevronTop = styled.div<{ side: 'top' | 'bottom' }>`
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  ${({ side }) => `border-${side}`}: 6px solid black;
`;
const ChevronRight = styled.div`
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid black;
  transform: rotate(-90deg);
`;

type Goal = {
  description: string;
  keyQuestions: string;
  maxLabel: string;
  minLabel: string;
  name: string;
  value: number;
};

const ScoringList = ({
  goals,
  onScoringSelectChange,
}: {
  goals: Goal[];
  onScoringSelectChange: () => void;
}) => {
  const isDesktop = useMedia(`(min-width: ${breakpoints.md}px)`);
  // By default we want the first panel to be opened on mobile
  const [activeIds, setActiveIds] = useState<string[]>(goals?.[0]?.name ? [goals[0]?.name] : []);
  const [isSliderOpened, setIsSliderOpened] = useState(false);
  const [value, setValue] = useState<Goal['name']>('');

  const handleClick = (goalName: Goal['name']) => {
    if (isDesktop) {
      setIsSliderOpened(true);
      setValue(goalName);
    }
    // const newActiveIds = [...activeIds, goal]; // for multiple collapse to be opened at the same time
    const newActiveIds = [goalName];
    if (!activeIds.includes(goalName)) {
      setActiveIds(newActiveIds);
    } else {
      setActiveIds(newActiveIds.filter((active) => active !== goalName));
    }
  };

  const closeSlider = () => {
    setIsSliderOpened(false);
    setValue('null');
  };

  const dataFrom = 'scorings';

  return (
    <>
      {!isSliderOpened && (
        <ScoringWrapper>
          {goals?.map((goal, i) => (
            <Fragment key={goal.name}>
              <ScoringLine
                data-click={goal.name.toLowerCase()}
                data-from={dataFrom}
                data-testid={`${goal.name}_btn`}
                isLast={i === goals.length - 1}
                onClick={() => handleClick(goal.name)}
                type="button"
              >
                <Typography align="left" color="noir" variant="p2">
                  {goal.name}
                </Typography>
                <ProgressLineWrapper>
                  <ProgressLine
                    // @ts-expect-error - ProgressLine not migrated yet (benoit)
                    backgroundColor={theme.colors.primary[100]}
                    color={theme.colors.primary[300]}
                    height="8px"
                    progress={goal.value}
                  />
                </ProgressLineWrapper>
                <CountContent className="remove-in-percy" count={goal.value}>
                  <CountUp duration={1} end={goal.value} start={0} />
                  /100
                </CountContent>
                {isDesktop && (
                  <ScoringChevronDesktop>
                    <ChevronRight />
                  </ScoringChevronDesktop>
                )}
              </ScoringLine>
              {!isDesktop && (
                <ScoringChevronMobile
                  data-click={goal.name.toLowerCase()}
                  data-from={dataFrom}
                  isDesktop={isDesktop}
                  onClick={() => handleClick(goal.name)}
                  type="button"
                >
                  <ChevronTop side={!activeIds.includes(goal.name) ? 'top' : 'bottom'} />
                </ScoringChevronMobile>
              )}
              <ScoringListContentMobile
                goal={goal}
                isActive={!isDesktop && activeIds.includes(goal.name)}
              />
            </Fragment>
          ))}
        </ScoringWrapper>
      )}
      {isSliderOpened && (
        <ScoringListContentDesktop
          goals={goals}
          onClose={closeSlider}
          onScoringChange={onScoringSelectChange}
          selectedValue={value}
        />
      )}
    </>
  );
};

export default ScoringList;
